/**
 * Submit-button protection.
 *
 * Locks a form's submit button while the form is being submitted and
 * guarantees it is unlocked again, regardless of how the form is wired
 * (HTMX-boosted XHR, native HTTP POST), what the response status is,
 * or how client-side validation is implemented.
 *
 * Usage
 * -----
 *
 *  1. Declarative (preferred):
 *
 *       <form data-protect-submit ...>
 *
 *  2. Imperative form protection:
 *
 *       import { protectForm } from '../utils/submit-button.js';
 *       protectForm(formEl);
 *
 *  3. Manual lock/unlock for async action buttons:
 *
 *       import { lockButton, unlockButton } from '../utils/submit-button.js';
 *       lockButton(btn);
 *       fetch(...).finally(() => unlockButton(btn));
 *
 * Architecture: lock on real "request started", unlock on "request done"
 * -----------------------------------------------------------------------
 *
 * The submit DOM event is *not* a reliable "the form is being submitted"
 * signal: many things can swallow it after we see it (custom client-side
 * validators calling ``e.preventDefault() + e.stopImmediatePropagation()``,
 * HTMX hijacking it, etc.). Heuristics like "did anyone call
 * preventDefault?" or "was the htmx-request class added?" depend on
 * listener registration order, which is not stable across forms.
 *
 * Instead we tie the spinner to the *outcome* of the submit pipeline:
 *
 *   * For HTMX-boosted forms — the vast majority — we lock in
 *     ``htmx:beforeRequest`` and unlock in ``htmx:afterRequest``.
 *     These events fire reliably whenever HTMX actually issues an XHR,
 *     no matter what client validators do, and ``htmx:afterRequest``
 *     always fires when the request ends (success, error, no-swap,
 *     network failure, abort).
 *
 *   * For native forms (``hx-boost="false"``) we listen on the bubble
 *     phase of ``submit``: by then every form-level listener has had
 *     its say. If ``defaultPrevented`` is still false the browser is
 *     about to navigate the page — we lock for the brief moment until
 *     the new page replaces the button.
 *
 * Two side effects we still need to handle generically:
 *
 *   * Double-submit guard. A capture-phase listener on ``document``
 *     swallows a second submit while the button is already locked,
 *     before HTMX or any other handler can run.
 *
 *   * Stale-lock rescue. After bfcache / HTMX history-restore / partial
 *     swaps a button can come back from a snapshot still wearing
 *     ``data-sb-locked``. Global ``pageshow``, ``htmx:historyRestore``
 *     and ``htmx:afterSwap`` listeners scrub these.
 */

const SPINNER_SVG =
    '<svg data-sb-spinner="1" class="animate-spin -ml-0.5 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">' +
    '<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>' +
    '<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>' +
    '</svg>';

const ORIGINAL_HTML_KEY = '_sbOriginalHtml';
const PROTECT_ATTR = 'data-protect-submit';
const SAFETY_TIMEOUT_MS = 15000;

/** Exported so other modules can build custom labels with the same look. */
export const SUBMIT_SPINNER_SVG = SPINNER_SVG;

/**
 * Lock a button: disable it, prepend the spinner to its label, and tag
 * it with ``data-sb-locked="1"``. Idempotent.
 *
 * @param {HTMLButtonElement|null|undefined} btn
 * @param {object} [options]
 * @param {string} [options.spinner]  Custom spinner HTML.
 * @param {string} [options.label]    Override the visible label while locked.
 */
export function lockButton(btn, options) {
    if (!btn || btn.dataset.sbLocked === '1') return;
    if (btn[ORIGINAL_HTML_KEY] === undefined) {
        btn[ORIGINAL_HTML_KEY] = btn.innerHTML;
    }
    var opts = options || {};
    var spinner = opts.spinner !== undefined && opts.spinner !== null ? opts.spinner : SPINNER_SVG;
    var label = opts.label !== undefined && opts.label !== null ? opts.label : btn[ORIGINAL_HTML_KEY];
    btn.innerHTML = spinner + label;
    btn.disabled = true;
    btn.classList.add('opacity-75', 'pointer-events-none');
    btn.dataset.sbLocked = '1';
}

/**
 * Restore a previously-locked button. Safe to call on a button that
 * was never locked.
 *
 * @param {HTMLButtonElement|null|undefined} btn
 */
export function unlockButton(btn) {
    if (!btn) return;
    if (btn[ORIGINAL_HTML_KEY] !== undefined) {
        btn.innerHTML = btn[ORIGINAL_HTML_KEY];
    } else {
        // History/bfcache restore: JS property is gone but the spinner
        // SVG is still in the markup. Strip it; the original label text
        // sits next to it untouched.
        var spinner = btn.querySelector('[data-sb-spinner="1"]');
        if (spinner) spinner.remove();
    }
    btn.disabled = false;
    btn.classList.remove('opacity-75', 'pointer-events-none');
    delete btn.dataset.sbLocked;
}

/**
 * Reset every locked button under ``root`` (default: whole document).
 *
 * @param {ParentNode} [root]
 */
export function unlockAll(root) {
    var scope = root || document;
    if (!scope || typeof scope.querySelectorAll !== 'function') return;
    scope.querySelectorAll('button[data-sb-locked="1"]').forEach(unlockButton);
}

/**
 * Mark a form as protected. Equivalent to writing ``<form data-protect-submit>``.
 *
 * @param {HTMLFormElement|null|undefined} formEl
 */
export function protectForm(formEl) {
    if (!formEl) return;
    if (!formEl.hasAttribute(PROTECT_ATTR)) {
        formEl.setAttribute(PROTECT_ATTR, '');
    }
}

/**
 * Back-compat shim. Auto-protection now happens via the global listeners
 * below; this is a no-op kept exported to avoid breaking callers.
 */
export function autoProtectForms() {
    /* no-op */
}

// ---------------------------------------------------------------------------
// Submit validators
// ---------------------------------------------------------------------------
//
// Page modules that need to do client-side validation on form submit
// (team-form, company-settings, email-settings, ...) used to register a
// bubble-phase listener directly on the form element via
// ``formEl.addEventListener('submit', fn)``. That makes their behaviour
// depend on listener registration order with HTMX's hx-boost handler,
// which is NOT stable:
//
//   * Hard refresh: htmx's DOMContentLoaded scan runs before app.js's
//     ``_runAutoInit`` → HTMX listener registered first → HTMX wins,
//     validator runs second too late.
//   * hx-boost navigation: ``htmx:afterSwap`` runs ``_runAutoInit``,
//     then settle phase calls ``processNode`` (which attaches HTMX's
//     listener) — see settleInfo.tasks loop in htmx swap. So validator
//     gets registered first → validator wins.
//
// Both regimes "work" but produce different observable behaviour (e.g.
// the spinner appearing on hard-refresh-then-submit but not on
// boost-then-submit). The fix is to register validators on ``document``
// in CAPTURE phase: capture runs on the path from window → document →
// form, BEFORE any AT_TARGET listener on the form (regardless of when
// they were attached). One deterministic insertion point, all forms.
//
// Validators that want to cancel the submission must call
// ``e.preventDefault()`` AND ``e.stopImmediatePropagation()`` themselves
// — capture phase doesn't auto-cancel sibling/target listeners.

const _validators = [];
let _validatorListenerBound = false;

function _runValidators(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    for (var i = 0; i < _validators.length && !event.defaultPrevented; i++) {
        var v = _validators[i];
        try {
            if (form.matches(v.selector)) v.fn(form, event);
        } catch (err) {
            // Don't let one validator's bug break the page's submit pipeline.
            if (typeof console !== 'undefined' && console.error) console.error(err);
        }
    }
}

/**
 * Register a client-side submit validator that runs in capture phase on
 * ``document``, BEFORE HTMX or any other form-level submit listener.
 * Idempotent for the listener wiring itself, but the same ``(selector,
 * fn)`` pair will be added twice if you call it twice — caller's job to
 * register once per page-module init.
 *
 * @param {string} selector  CSS selector matching the form(s) to validate.
 * @param {(form: HTMLFormElement, event: SubmitEvent) => void} fn
 *        Called with the form and submit event. Call
 *        ``event.preventDefault()`` AND ``event.stopImmediatePropagation()``
 *        to cancel the submission.
 */
export function registerSubmitValidator(selector, fn) {
    if (!selector || typeof fn !== 'function') return;
    _validators.push({ selector: selector, fn: fn });
    if (!_validatorListenerBound && typeof document !== 'undefined') {
        _validatorListenerBound = true;
        document.addEventListener('submit', _runValidators, true);
    }
}

function isProtectedForm(elt) {
    return !!(elt && elt.tagName === 'FORM' && elt.hasAttribute(PROTECT_ATTR));
}

function findSubmitButton(form, triggeringEvent) {
    if (triggeringEvent && triggeringEvent.submitter && triggeringEvent.submitter.tagName === 'BUTTON') {
        // Use the actual submitter when present (multi-submit-button forms).
        if (triggeringEvent.submitter.form === form) {
            return triggeringEvent.submitter;
        }
    }
    return form.querySelector('button[type="submit"]');
}

function armSafetyTimeout(btn) {
    // Defensive only: every realistic completion path already unlocks.
    setTimeout(function () { unlockButton(btn); }, SAFETY_TIMEOUT_MS);
}

// ---------------------------------------------------------------------------
// Lock paths
// ---------------------------------------------------------------------------
//
// Optimistic lock at the very start of the submit dispatch (document
// capture, registered FIRST so it wins against any validator capture
// listener registered later via ``registerSubmitValidator``). Gives the
// user immediate spinner feedback the moment they click. After the
// synchronous dispatch completes, a microtask decides whether to keep
// the lock:
//
//   * htmx-request class is set on the form → HTMX took over the
//     submit, the XHR is in flight; ``htmx:afterRequest`` will unlock.
//   * defaultPrevented but no htmx-request → a validator cancelled the
//     submit (no work in flight). Unlock so the user can fix the field.
//   * defaultPrevented=false, no htmx-request → native form POST that
//     will navigate the page. Leave the button locked; the new page
//     will replace it.
//
// This heuristic is reliable specifically *because* validators are now
// registered via ``registerSubmitValidator`` in document capture phase.
// Their order relative to HTMX's at-target listener is deterministic
// (capture always comes first, AT_TARGET second), regardless of whether
// the page was reached via hard refresh or hx-boost navigation.

function handleSubmitCapture(event) {
    var form = event.target;
    if (!isProtectedForm(form)) return;
    var btn = findSubmitButton(form, event);
    if (!btn) return;

    // Double-submit guard.
    if (btn.dataset.sbLocked === '1') {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
    }

    lockButton(btn);
    armSafetyTimeout(btn);

    Promise.resolve().then(function () {
        if (form.classList.contains('htmx-request')) return;
        if (event.defaultPrevented) {
            unlockButton(btn);
        }
        // else: native form POST in flight, leave locked until navigation.
    });
}

// ---------------------------------------------------------------------------
// Unlock paths
// ---------------------------------------------------------------------------

function handleHtmxAfterRequest(event) {
    var elt = event.detail && event.detail.elt;
    if (elt) unlockAll(elt);
}

function handleHtmxResponseError(event) {
    var elt = event.detail && event.detail.elt;
    if (elt) unlockAll(elt);
}

function handleHtmxSendError(event) {
    var elt = event.detail && event.detail.elt;
    if (elt) unlockAll(elt);
}

function handleHtmxAfterSwap(event) {
    var target = event && event.detail && event.detail.target;
    unlockAll(target || undefined);
}

function resetIfStale() {
    unlockAll();
}

// ---------------------------------------------------------------------------
// One-shot global wiring. ``document`` and ``window`` are never swapped,
// so a single registration here covers every protected form forever.
// ---------------------------------------------------------------------------

if (typeof window !== 'undefined') {
    // Single capture-phase listener handles BOTH the optimistic lock
    // and the post-dispatch "should we keep it locked?" decision.
    document.addEventListener('submit', handleSubmitCapture, true);

    // HTMX request lifecycle: any in-flight XHR ending (success, 4xx,
    // 5xx, network failure, abort) unlocks the requesting form's
    // submit button. Scoped to ``detail.elt`` to avoid touching
    // unrelated forms when a sibling triggers an XHR.
    document.addEventListener('htmx:afterRequest', handleHtmxAfterRequest);
    document.addEventListener('htmx:responseError', handleHtmxResponseError);
    document.addEventListener('htmx:sendError', handleHtmxSendError);

    // Swap-driven rescue: after a partial swap brings new content in,
    // any locked button that survived the swap gets reset.
    document.addEventListener('htmx:afterSwap', handleHtmxAfterSwap);

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', resetIfStale);
    } else {
        resetIfStale();
    }
    window.addEventListener('pageshow', resetIfStale);
    document.addEventListener('htmx:historyRestore', resetIfStale);
}
